import { reactular } from '@vendhq/reactular'
import { AblyProvider, ChannelProvider, useChannel } from 'ably/react'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { PAYMENT_TYPES } from '@vend/business-components'
import {
  useIsCalculatingSale,
  useRegisterManager,
} from '../../../ng-compat/hooks'
import {
  createOnAccountSaleCompletedPayload,
  createSaleHiddenPayload,
  createSalePaymentReceivedOrCompletedPayload,
  createSaleUpdatedPayload,
} from '../../../state/selectors/cfdEventPayloadSelectors'
import { RootState } from '../../../state/store'
import {
  RealtimeCfdProvider,
  useRealtimeCfd,
} from '../../customer-facing-display/RealtimeCfdProvider'

import { useAppSelector } from '../../../state/hooks'
import {
  selectCurrentSale,
  selectSalePayments,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  selectActiveOutletId,
  selectActiveRegisterId,
} from '../../../state/slices/posState/posStateSlice'
import { CFDEvents } from '../../../util/cfd-types'
import { SaleStatus } from '../../../models/sale.constants'

const STALE_PAYMENT_THRESHOLD_MS = 5000
const LSPAY_CHANNEL_PREFIX = 'lspay:terminal:'

type SaleChangeEventsProps = {
  serialNumber: string
}

export const SaleChangeEvents: React.FunctionComponent<SaleChangeEventsProps> = ({
  serialNumber,
}) => {
  const { channel, publish } = useChannel(
    `${LSPAY_CHANNEL_PREFIX}${serialNumber}`
  )

  useEffect(() => {
    channel.presence.enter()
  }, [channel])

  const isRootPath = (url: string) =>
    url === '/webregister' ||
    url === '/webregister/' ||
    url === '/webregister/confirmation'
  const [isOnRootPage, setIsOnRootPage] = useState(
    isRootPath(window.location.pathname)
  )
  const previousPaymentsSumRef = useRef<typeof paymentsSum>(0)
  const registerManager = useRegisterManager()
  const currentSale = useSelector(selectCurrentSale)
  const payments = useSelector(selectSalePayments)

  const salePayload = useSelector((state: RootState) =>
    createSaleUpdatedPayload(state, registerManager)
  )

  const prevSalePayload = useRef(salePayload.data)

  const paymentsSum = payments.reduce(
    (total, payment) => total + Number(payment.amount),
    0
  )

  const saleTotal =
    Number(currentSale.finals.inclusivePrice) +
    Number(currentSale.payAccountBalance?.amount || 0)

  const previousPaymentsRef = useRef<typeof payments>([])

  const isCalculatingSale = useIsCalculatingSale()
  const prevIsCalculatingSale = useRef<boolean>(isCalculatingSale)

  /** React to navigation changes */
  useEffect(() => {
    const handleInAppNavigation = (event: any) => {
      setIsOnRootPage(
        event.detail.to === '/' ||
          event.detail.to === undefined ||
          event.detail.to === '/confirmation'
      )
      if (
        event.detail.from === '/' &&
        event.detail.to &&
        event.detail.to !== '/'
      ) {
        const saleHiddenPayload = createSaleHiddenPayload()
        publish(CFDEvents.SALE_HIDDEN, saleHiddenPayload)
      }

      // Handle navigation back to the root page within the webregister
      if (
        event.detail.to &&
        event.detail.to === '/' &&
        event.detail.from &&
        event.detail.from !== '/confirmation'
      ) {
        publish(CFDEvents.SALE_UPDATED, salePayload)
      }
    }

    window.addEventListener('angular-navigation-start', handleInAppNavigation)
    return () => {
      window.removeEventListener(
        'angular-navigation-start',
        handleInAppNavigation
      )
    }
  }, [publish, setIsOnRootPage, salePayload])

  /** React to changes in the sale line items, discounts, fulfillment and account balances payments */
  useEffect(() => {
    const prevLineItems = new Map(
      prevSalePayload.current.lineItems.map(item => [item.id, item])
    )

    const wasFulfillmentAdded = salePayload.data.lineItems.some(
      item =>
        item.fulfillmentType !== prevLineItems.get(item.id)?.fulfillmentType
    )

    const wasLineItemsCleared =
      prevSalePayload.current.lineItems.length > 0 &&
      salePayload.data.lineItems.length === 0

    const wasSaleRecalculated =
      prevIsCalculatingSale.current && !isCalculatingSale

    const wasDiscountUpdated =
      prevSalePayload.current.discount?.amount !==
        salePayload.data.discount?.amount ||
      prevSalePayload.current.discount?.percentage !==
        salePayload.data.discount?.percentage

    const wasAccountPaymentUpdated =
      prevSalePayload.current.accountPayment !== salePayload.data.accountPayment

    const shouldPublishSaleUpdated =
      isOnRootPage &&
      (wasSaleRecalculated ||
        wasLineItemsCleared ||
        wasFulfillmentAdded ||
        wasDiscountUpdated ||
        wasAccountPaymentUpdated)

    if (shouldPublishSaleUpdated) {
      publish(CFDEvents.SALE_UPDATED, salePayload)
    }

    prevIsCalculatingSale.current = isCalculatingSale
    prevSalePayload.current = salePayload.data
  }, [salePayload, publish, isOnRootPage, isCalculatingSale])

  /** React to sale balance paid in full or sale closed as on-account */
  useEffect(() => {
    const isOnAccountSale = currentSale.status === SaleStatus.ACCOUNT_SALE

    if (isOnAccountSale) {
      createOnAccountSaleCompletedPayload(registerManager).then(
        saleCompletedPayload => {
          publish(CFDEvents.SALE_COMPLETED, saleCompletedPayload)
        }
      )
      return
    }

    if (paymentsSum === previousPaymentsSumRef.current) {
      return
    }

    const payment = payments[payments.length - 1]
    if (Math.abs(paymentsSum) < Math.abs(saleTotal) || !payment) {
      return
    }

    createSalePaymentReceivedOrCompletedPayload(
      currentSale,
      payment,
      registerManager,
      CFDEvents.SALE_COMPLETED
    ).then(saleCompletedPayload => {
      publish(CFDEvents.SALE_COMPLETED, saleCompletedPayload)
    })
  }, [currentSale, payments, publish, registerManager, saleTotal, paymentsSum])

  /** React to payments received */
  useEffect(() => {
    if (Math.abs(paymentsSum) >= Math.abs(saleTotal)) {
      return
    }
    const prevPayments = previousPaymentsRef.current

    // Find new payments by comparing localIds
    const prevPaymentIds = new Set(prevPayments.map(p => p.localId))
    const newPayments = payments.filter(
      p =>
        !prevPaymentIds.has(p.localId) &&
        // Ignore payments that are older than the threshold to avoid duplicates
        Date.now() - new Date(p.paymentDate).getTime() <
          STALE_PAYMENT_THRESHOLD_MS
    )

    // Only process if we have new payments
    if (newPayments.length > 0) {
      // Generate SALE_PAYMENT_RECEIVED event for each new payment, excluding LOYALTY and STORE_CREDIT
      newPayments.forEach(payment => {
        if (
          payment.paymentType.type_id === PAYMENT_TYPES.LOYALTY ||
          payment.paymentType.type_id === PAYMENT_TYPES.STORE_CREDIT
        ) {
          publish(CFDEvents.SALE_UPDATED, salePayload)
          return
        }
        createSalePaymentReceivedOrCompletedPayload(
          currentSale,
          payment,
          registerManager,
          CFDEvents.SALE_PAYMENT_RECEIVED
        ).then(paymentReceivedPayload => {
          publish(CFDEvents.SALE_PAYMENT_RECEIVED, paymentReceivedPayload)
          publish(CFDEvents.SALE_UPDATED, salePayload)
        })
      })
    }

    // Update the ref AFTER we've done the comparison
    previousPaymentsRef.current = payments
  }, [
    currentSale,
    payments,
    publish,
    registerManager,
    saleTotal,
    paymentsSum,
    salePayload,
  ])

  // Update the ref AFTER we've done the comparison
  // this should always be placed after any useEffect that compares
  // paymentsSum to previousPaymentsSumRef.current
  useEffect(() => {
    previousPaymentsSumRef.current = paymentsSum
  }, [paymentsSum])

  return null
}

const SaleChangeAblyEventHandler: React.FC = () => {
  const { client, serialNumber } = useRealtimeCfd()
  if (!client || !serialNumber) {
    return null
  }
  return (
    <AblyProvider client={client}>
      <ChannelProvider channelName={`${LSPAY_CHANNEL_PREFIX}${serialNumber}`}>
        <SaleChangeEvents serialNumber={serialNumber} />
      </ChannelProvider>
    </AblyProvider>
  )
}

const SaleChangeListener: React.FC = () => {
  const outletId = useAppSelector(selectActiveOutletId)
  const registerId = useAppSelector(selectActiveRegisterId)

  if (!registerId || !outletId) {
    return null
  }

  return (
    <RealtimeCfdProvider outletId={outletId} registerId={registerId}>
      <SaleChangeAblyEventHandler />
    </RealtimeCfdProvider>
  )
}

export const ReactSaleChangeListenerComponent = reactular(
  () => <SaleChangeListener />,
  [],
  'reactWrapper'
)
